import { Route, Routes, useLocation } from 'react-router-dom';
import { useStore } from 'hooks/useStore';
import { useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';

function deriveModule(pathname: string): string {
  if (pathname.startsWith('/bdportal/bid-analysis')) return 'bid_analysis';
  if (pathname.startsWith('/bdportal/optracker')) return 'opp_tracker';
  if (pathname.startsWith('/bdportal/product-wiki')) return 'product_wiki';
  if (pathname.startsWith('/bdportal')) return 'bd_portal';
  if (pathname.startsWith('/facts')) return 'facts';
  if (pathname.startsWith('/fdp')) return 'fdp';
  return 'home';
}

import ConstraintForecastHoc from 'Hoc/ConstraintForecast';
import { FactsScreenHOC } from 'Hoc/factsScreenHoc';
import DrugPortfolioManagementHoc from 'Hoc/drugPortfolioManagementHoc';
import FdpScreenHoc from 'Hoc/fdpScreenHoc';
import SupplierForecastHoc from 'Hoc/SupplierForecastHoc';
import TargetMarketShareHoc from 'Hoc/targetMarketShareHoc';
import UnconstraintForecastHoc from 'Hoc/UnconstraintForecastHoc';
import BDPortalHoc from 'Hoc/BDPortalHoc';
import BidAnalysisHoc from 'Hoc/BidAnalysisHoc';
import AuthenticationGuardHoc from 'Hoc/AuthenticationGuardHoc';
import FlaggedRoute from 'components/FlaggedRoute/FlaggedRoute';
import PublicFlaggedRoute from 'components/PublicFlaggedRoute/PublicFlaggedRoute';
import PreviewScreen from 'screens/PreviewScreen';
import HomeScreen from 'screens/HomeScreen';

import FactsScreen from 'screens/FactsScreen';
import FactsHomeScreen from 'screens/FactsHomeScreen/FactsHome';
import DrugPortfolioManagement from 'screens/DrugPortfolioManagement';

import FDPScreen from 'screens/FDPScreen';
import TMSEditComponent from 'screens/FDPScreen/components/TargetMarketShare/TargetMarketShareEditScreen';
import TargetMarketShareScreen from 'screens/FDPScreen/components/TargetMarketShare/TargetMarketShareScreen';
import UnconstraintForecast from 'screens/FDPScreen/components/UnconstraintForecastScreen';
import UnconstraintForecastEdit from 'screens/FDPScreen/components/UnconstraintForecastScreen/components/UnconstraintForecastEdit';
import ConstraintForecast from 'screens/FDPScreen/components/ConstraintForecastScreen';
import ConstraintForecastEdit from 'screens/FDPScreen/components/ConstraintForecastScreen/components/ConstraintForecastEdit';
import ProvisionalForecastV2Hoc from 'Hoc/ProvisionalForecastV2Hoc';
import ProvisionalForecastV2Landing from 'screens/FDPScreen/components/ProvisionalForecastV2/Landing';
import ProvisionalForecastV2Edit from 'screens/FDPScreen/components/ProvisionalForecastV2/Edit';

import BDPortalLanding from 'screens/BDPortal/LandingPage/BDPortalLanding';
import MultiKanbanBoard from 'screens/BDPortal/OpportunityTracker/components/MultiKanbanBoard/MultiKanbandBoard';
import MultiKanbanList from 'screens/BDPortal/OpportunityTracker/components/MultiKanbanList/OpportunityKanbanList';
import PortfolioOpportuntiesListScreen from 'screens/BDPortal/OpportunityTracker/components/PortfolioOpportuntiesListScreen/PortfolioOpportuntiesListScreen';
import SupplierForecast from 'screens/FDPScreen/components/SupplierForecast';
import LoginFailureScreen from 'screens/LoginFailureScreen/loginFailureScreen';
import LoginCallback from 'screens/LoginCallback';
import { checkIfTokenExpired } from 'utils/AuthUtils/tokenUtils';
import SessionExpiryModal from 'components/SessionExpiryModal/SessionExpiryModal';
import BulkEditRoot from 'screens/FDPScreen/components/UnconstraintForecastScreen/components/BulkEdit/bulkEditRoot';
import ConstrainedForecastBulkEdit from 'screens/FDPScreen/components/ConstraintForecastScreen/components/ConstrainedForecastBulkEdit/constrainedForecastBulkEdit';
import MSPlanBulkEditScreen from 'screens/FDPScreen/components/TargetMarketShare/BulkEditScreens/MSPlanBulkEditScreen/MSPlanBulkEditScreen';
import SalesPlanBulkEditScreen from 'screens/FDPScreen/components/TargetMarketShare/BulkEditScreens/SalesPlanBulkEditScreen/SalesPlanBulkEditScreen';

// Bid Analysis imports
import BidAnalysisDashboard from 'screens/BidAnalysis/BidAnalysisDashboard/BidAnalysisDashboard';
import SelectContractType from 'screens/BidAnalysis/Wizard/SelectContractType/SelectContractType';
import SelectOpportunity from 'screens/BidAnalysis/Wizard/SelectOpportunity/SelectOpportunity';
import SelectProductType from 'screens/BidAnalysis/Wizard/SelectProductType/SelectProductType';
import Confirmation from 'screens/BidAnalysis/Wizard/Confirmation/Confirmation';
import BidAnalysisTemplate from 'screens/BidAnalysis/BidAnalysisTemplate/BidAnalysisTemplate';
import BidReviewTemplate from 'screens/BidAnalysis/BidReviewTemplate/BidReviewTemplate';
import BidValidationTemplate from 'screens/BidAnalysis/BidValidationTemplate/BidValidationTemplate';
import ReferenceTables from 'screens/BidAnalysis/ReferenceTables/ReferenceTables';
import ManagerReview from 'screens/BidAnalysis/ManagerReview/ManagerReview';
import ProductWikiLandingPage from 'screens/BDPortal/ProductWiki/components/LandingPage/landingPage';
import MoleculeSearch from 'screens/BDPortal/ProductWiki/components/MoleculeSearchResults/moleculeSearch';
import ProductAnalysis from 'screens/BDPortal/ProductWiki/components/ProductAnalysis';
import OOCFdpHoc from 'Hoc/OOCFdpHoc';
import OOCForecast from 'screens/FDPScreen/components/OOCForecast';
import OOCUpdatedHoc from 'Hoc/OOCUpdatedHoc';
import OOCUpdatedScreen from 'screens/FDPUpdatedScreen/components/OOCUpdated';
import FdpUpdatedScreenHoc from 'Hoc/FdpUpdatedScreenHoc';
import FDPUpdatedScreen from 'screens/FDPUpdatedScreen';
import SalesPlanUpdatedHoc from 'Hoc/SalesPlanUpdatedHoc';
import SalesPlanUpdatedView from 'screens/FDPUpdatedScreen/components/SalesPlanUpdated/SalesPlanView';
import SalesPlanUpdatedEdit from 'screens/FDPUpdatedScreen/components/SalesPlanUpdated/SalesPlanEdit';
import BulkEditUpdatedHoc from 'Hoc/BulkEditUpdatedHoc';
import BulkEditUpdated from 'screens/FDPUpdatedScreen/components/BulkEditUpdated';
import SalesPlanBulkEditUpdatedHoc from 'Hoc/SalesPlanBulkEditUpdatedHoc';
import SalesPlanBulkEditUpdated from 'screens/FDPUpdatedScreen/components/SalesPlanBulkEditUpdated';
import UnconstrainedForecastUpdatedHoc from 'Hoc/UnconstrainedForecastUpdatedHoc';
import UnconstrainedForecastUpdatedView from 'screens/FDPUpdatedScreen/components/UnconstrainedForecastUpdated/UnconstrainedView';
import UnconstrainedForecastUpdatedEdit from 'screens/FDPUpdatedScreen/components/UnconstrainedForecastUpdated/UnconstrainedEdit';
import ConstrainedForecastUpdatedHoc from 'Hoc/ConstrainedForecastUpdatedHoc';
import ConstrainedForecastUpdatedView from 'screens/FDPUpdatedScreen/components/ConstrainedForecastUpdated/ConstrainedView';
import ConstrainedForecastUpdatedEdit from 'screens/FDPUpdatedScreen/components/ConstrainedForecastUpdated/ConstrainedEdit';

const AppRoutes = () => {
  const {
    UserStore: { fetchUserDetails, isUserLoggedIn },
  } = useStore();
  const location = useLocation();

  // Tag every Sentry event with which module and route the user is on
  useEffect(() => {
    const module = deriveModule(location.pathname);
    Sentry.setTag('module', module);
    Sentry.setTag('route', location.pathname);
  }, [location.pathname]);

  const checkTokenExpiry = useCallback(() => {
    if (isUserLoggedIn() && checkIfTokenExpired()) {
      const sessionExpiryModal = document.getElementById('session-expired-modal');
      if (sessionExpiryModal) {
        sessionExpiryModal.style.display = 'flex';
      }
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    const expiryTimeCheckInterval = setInterval(checkTokenExpiry, 1000);
    return () => {
      clearInterval(expiryTimeCheckInterval);
    };
  }, [checkTokenExpiry]);

  useEffect(() => {
    if (isUserLoggedIn()) {
      fetchUserDetails();
    }
  }, [isUserLoggedIn, fetchUserDetails]);

  return (
    <div style={{ padding: 'none', margin: 'none' }}>
      <Routes>
        <Route path="" element={<HomeScreen />} />
        <Route path="login-failure" element={<LoginFailureScreen />} />
        <Route path="login/callback" element={<LoginCallback />} />
        <Route element={<PublicFlaggedRoute flagKey="mockups.storybook" />}>
          <Route path="preview" element={<PreviewScreen />} />
        </Route>
        <Route element={<AuthenticationGuardHoc />}>
          {/* FACTS module — landing is ungated; sub-features individually flagged */}
          <Route path="/facts" element={<FactsScreenHOC />}>
            <Route path="" element={<FactsHomeScreen />} />
            <Route element={<FlaggedRoute flagKey="facts.active_drug_strategy" />}>
              <Route path="active_drug_strategy" element={<FactsScreen />} />
            </Route>
          </Route>
          <Route element={<FlaggedRoute flagKey="facts.drug_portfolio" />}>
            <Route path="/facts/drug_portfolio_management" element={<DrugPortfolioManagementHoc />}>
              <Route path="" element={<DrugPortfolioManagement />} />
            </Route>
          </Route>

          {/* FDP module — landing is ungated; sub-features individually flagged */}
          <Route path="/fdp" element={<FdpScreenHoc />}>
            <Route path="" element={<FDPScreen />} />
          </Route>
          <Route element={<FlaggedRoute flagKey="fdp.tms" />}>
            <Route path="fdp/target_market" element={<TargetMarketShareHoc />}>
              <Route path="" element={<TargetMarketShareScreen />} />
              <Route path="edit" element={<TMSEditComponent />} />
              <Route path="bulk_edit/msplan" element={<MSPlanBulkEditScreen />} />
              <Route path="bulk_edit/salesplan" element={<SalesPlanBulkEditScreen />} />
            </Route>
          </Route>
          <Route element={<FlaggedRoute flagKey="fdp.unconstrained_forecast" />}>
            <Route path="fdp/unconstrained_forecast" element={<UnconstraintForecastHoc />}>
              <Route path="" element={<UnconstraintForecast />} />
              <Route path="edit/:gfc" element={<UnconstraintForecastEdit />} />
              <Route path="bulk_edit" element={<BulkEditRoot />} />
            </Route>
          </Route>
          <Route element={<FlaggedRoute flagKey="fdp.constrained_forecast" />}>
            <Route path="fdp/constrained_forecast" element={<ConstraintForecastHoc />}>
              <Route path="" element={<ConstraintForecast />} />
              <Route path="edit/:gfc" element={<ConstraintForecastEdit />} />
              <Route path="bulk_edit" element={<ConstrainedForecastBulkEdit />} />
            </Route>
          </Route>
          <Route element={<FlaggedRoute flagKey="fdp.provisional_forecast" />}>
            <Route path="fdp/provisonal_forecast" element={<ProvisionalForecastV2Hoc />}>
              <Route path="" element={<ProvisionalForecastV2Landing />} />
              <Route path="edit" element={<ProvisionalForecastV2Edit />} />
            </Route>
          </Route>
          <Route element={<FlaggedRoute flagKey="fdp.supplier_forecast" />}>
            <Route path="fdp/supplier_forecast" element={<SupplierForecastHoc />}>
              <Route path="" element={<SupplierForecast />} />
            </Route>
          </Route>
          <Route path="fdp/ooc" element={<OOCFdpHoc />}>
            <Route path="" element={<OOCForecast />} />
          </Route>

          {/* BD Portal — landing is ungated; sub-modules individually flagged */}
          <Route path="/bdportal" element={<BDPortalHoc />}>
            <Route path="" element={<BDPortalLanding />} />
            <Route path="/bdportal/optracker" element={<FlaggedRoute flagKey="bdportal.opportunity_tracker" />}>
              <Route path="/bdportal/optracker/board" element={<MultiKanbanBoard />} />
              <Route path="/bdportal/optracker/list" element={<MultiKanbanList />} />
              <Route
                path="/bdportal/optracker/portfolio-opportunities"
                element={<PortfolioOpportuntiesListScreen />}
              />
            </Route>
            <Route path="/bdportal/product-wiki" element={<FlaggedRoute flagKey="bdportal.product_wiki" />}>
              <Route path="" element={<ProductWikiLandingPage />} />
              <Route path="molecule-search" element={<MoleculeSearch />} />
              <Route path="product-analysis" element={<ProductAnalysis />} />
            </Route>
          </Route>
          <Route element={<FlaggedRoute flagKey="bdportal.bid_analysis" />}>
            <Route path="/bdportal/bid-analysis" element={<BidAnalysisHoc />}>
              <Route path="" element={<BidAnalysisDashboard />} />
              <Route path="dashboard" element={<BidAnalysisDashboard />} />
              <Route path="manager-review" element={<ManagerReview />} />
              <Route path="template/:bidId" element={<BidAnalysisTemplate />} />
              <Route path="review/:bidId" element={<BidReviewTemplate />} />
              <Route path="validate/:bidId" element={<BidValidationTemplate />} />
              <Route path="wizard">
                <Route path="contract-type" element={<SelectContractType />} />
                <Route path="opportunity" element={<SelectOpportunity />} />
                <Route path="product-type" element={<SelectProductType />} />
                <Route path="confirmation" element={<Confirmation />} />
              </Route>
              <Route path="reference-tables" element={<ReferenceTables />} />
            </Route>
          </Route>
          {/* <Route path="/support-tickets" element={<SupportTicketsScreen />} /> */}

          {/* FDP Updated module — requires authentication + mockups.storybook flag */}
          <Route element={<FlaggedRoute flagKey="mockups.storybook" />}>
            <Route path="/fdp-updated" element={<FdpUpdatedScreenHoc />}>
              <Route path="" element={<FDPUpdatedScreen />} />
            </Route>
            <Route path="fdp-updated/sales_plan_updated" element={<SalesPlanUpdatedHoc />}>
              <Route path="" element={<SalesPlanUpdatedView />} />
              <Route path="edit" element={<SalesPlanUpdatedEdit />} />
            </Route>
            <Route path="fdp-updated/unconstrained_forecast" element={<UnconstrainedForecastUpdatedHoc />}>
              <Route path="" element={<UnconstrainedForecastUpdatedView />} />
              <Route path="edit" element={<UnconstrainedForecastUpdatedEdit />} />
            </Route>
            <Route path="fdp-updated/constrained_forecast" element={<ConstrainedForecastUpdatedHoc />}>
              <Route path="" element={<ConstrainedForecastUpdatedView />} />
              <Route path="edit" element={<ConstrainedForecastUpdatedEdit />} />
            </Route>
            <Route path="fdp-updated/unconstrained_bulk_edit" element={<BulkEditUpdatedHoc />}>
              <Route path="" element={<BulkEditUpdated />} />
            </Route>
            <Route path="fdp-updated/sales_plan_bulk_edit" element={<SalesPlanBulkEditUpdatedHoc />}>
              <Route path="" element={<SalesPlanBulkEditUpdated />} />
            </Route>
            <Route path="fdp-updated/ooc" element={<OOCUpdatedHoc />}>
              <Route path="" element={<OOCUpdatedScreen />} />
            </Route>
          </Route>
        </Route>
      </Routes>
      <SessionExpiryModal />
    </div>
  );
};

export default AppRoutes;
